import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Plus, Pencil, Trash2, ExternalLink, Package, FolderKanban, Users, Inbox, Images, Image as ImageIcon, Film, Award, FileText, Loader2, UserCog, KeyRound, Send, Copy, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import {
  listItems, upsertItem, deleteItem, logoutAdmin,
  listMedia, deleteMedia, uploadFile,
  listUsers, createUser, updateUser, deleteUser,
  listCampaignTokens, createCampaignToken, deleteCampaignToken,
  listDistribuitori,
} from '../../api';
import { fileUrl } from '../../api';
import { ImageField, MediaManager } from '../../components/admin/Uploaders';
import { COMPANY } from '../../mock';

const TABS = [
  { key: 'products', label: 'Produse', icon: Package },
  { key: 'projects', label: 'Proiecte', icon: FolderKanban },
  { key: 'clients', label: 'Clienți', icon: Users },
  { key: 'leads', label: 'Cereri ofertă', icon: Inbox },
  { key: 'media', label: 'Media', icon: Images },
  { key: 'users', label: 'Utilizatori', icon: UserCog },
  { key: 'campaigns', label: 'Campanii', icon: Send },
];

const FIELDS = {
  products: [
    { k: 'name', label: 'Nume produs', req: true },
    { k: 'category', label: 'Categorie' },
    { k: 'tag', label: 'Etichetă (tag)' },
    { k: 'useCase', label: 'Utilizare' },
    { k: 'price', label: 'Preț' },
    { k: 'image', label: 'Imagine principală', type: 'image' },
    { k: 'description', label: 'Descriere', area: true },
  ],
  projects: [
    { k: 'title', label: 'Titlu proiect', req: true },
    { k: 'type', label: 'Tip' },
    { k: 'year', label: 'An' },
    { k: 'location', label: 'Locație' },
    { k: 'image', label: 'Imagine principală', type: 'image' },
    { k: 'description', label: 'Descriere', area: true },
  ],
  clients: [
    { k: 'name', label: 'Nume client', req: true },
    { k: 'logo', label: 'Logo', type: 'image' },
  ],
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('products');
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // object being edited (dialog open)
  const [counts, setCounts] = useState({ products: 0, projects: 0, clients: 0, leads: 0, media: 0, users: 0, campaigns: 0 });

  const refreshCounts = async () => {
    try {
      const [p, pr, c, l, m, u, ct] = await Promise.all([
        listItems('products'), listItems('projects'), listItems('clients'), listItems('leads'), listMedia(), listUsers(), listCampaignTokens(),
      ]);
      setCounts({ products: p.length, projects: pr.length, clients: c.length, leads: l.length, media: m.length, users: u.length, campaigns: ct.length });
    } catch (e) { /* ignore */ }
  };

  const load = async () => {
    if (tab === 'media' || tab === 'users' || tab === 'campaigns') { setItems([]); return; }
    try {
      const data = await listItems(tab);
      setItems(data);
    } catch (e) {
      toast({ title: 'Eroare la încărcare', description: 'Verifică sesiunea și reîncearcă.' });
    }
  };
  useEffect(() => { load(); refreshCounts(); /* eslint-disable-next-line */ }, [tab]);

  const logout = () => { logoutAdmin(); navigate('/admin/login'); };

  const onSave = async () => {
    const fields = FIELDS[tab];
    const reqMissing = fields.filter((f) => f.req && !editing[f.k]).length > 0;
    if (reqMissing) { toast({ title: 'Completează câmpurile obligatorii' }); return; }
    try {
      await upsertItem(tab, editing);
      setEditing(null);
      await load();
      await refreshCounts();
      toast({ title: 'Salvat', description: 'Modificările sunt vizibile pe site.' });
    } catch (e) {
      toast({ title: 'Eroare la salvare' });
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteItem(tab, id);
      await load();
      await refreshCounts();
      toast({ title: 'Șters' });
    } catch (e) {
      toast({ title: 'Eroare la ștergere' });
    }
  };

  const newItem = () => {
    const base = {};
    (FIELDS[tab] || []).forEach((f) => (base[f.k] = ''));
    setEditing(base);
  };

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-[240px] border-r border-[#1e2025] bg-[#0a0b0d] p-5 fixed inset-y-0">
        <Link to="/" className="flex items-center gap-2.5 mb-8">
          <div className="h-9 w-9 rounded-md bg-[#f5a524] flex items-center justify-center">
            <span className="font-display font-bold text-[#131313] text-lg">E</span>
          </div>
          <div className="font-display font-bold text-[15px]">{COMPANY.name}<span className="text-[#f5a524]">.</span>Admin</div>
        </Link>
        <nav className="flex flex-col gap-1 flex-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-[14px] transition-colors ${
                  active ? 'bg-[#f5a524]/12 text-[#f5a524]' : 'text-[#a7abb2] hover:text-white hover:bg-[#141518]'
                }`}
              >
                <span className="flex items-center gap-2.5"><Icon size={17} /> {t.label}</span>
                <span className={`text-[11px] rounded-full px-2 py-0.5 ${active ? 'bg-[#f5a524]/20' : 'bg-[#1e2025]'}`}>{counts[t.key]}</span>
              </button>
            );
          })}
        </nav>
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[13px] text-[#8b8f97] hover:text-white transition-colors mb-3">
          <ExternalLink size={15} /> Vezi site-ul
        </a>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 text-[13px] text-[#8b8f97] hover:text-[#ef5350] transition-colors">
          <LogOut size={15} /> Deconectare
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-[240px] p-6 md:p-10">
        {/* Mobile tabs */}
        <div className="md:hidden flex gap-2 overflow-x-auto mb-6 pb-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-[13px] border ${tab === t.key ? 'bg-[#f5a524] text-[#131313] border-[#f5a524]' : 'border-[#26282e] text-[#c7cace]'}`}>
              {t.label} ({counts[t.key]})
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-bold text-[28px] leading-none">{TABS.find((t) => t.key === tab).label}</h1>
            <p className="text-[13px] text-[#8b8f97] mt-1.5">{tab === 'media' ? `${counts.media} fișiere` : tab === 'users' ? `${counts.users} utilizatori` : tab === 'campaigns' ? `${counts.campaigns} tokenuri` : `${items.length} înregistrări`}</p>
          </div>
          <div className="flex items-center gap-2">
            {tab !== 'leads' && tab !== 'media' && tab !== 'users' && tab !== 'campaigns' && (
              <button onClick={newItem} className="btn-accent rounded-md px-4 py-2.5 text-[14px] flex items-center gap-1.5">
                <Plus size={16} /> Adaugă
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {tab === 'leads' ? (
          <LeadsTable leads={items} onDelete={onDelete} />
        ) : tab === 'media' ? (
          <MediaLibrary onChange={refreshCounts} />
        ) : tab === 'users' ? (
          <UtilizatoriManager onChange={refreshCounts} />
        ) : tab === 'campaigns' ? (
          <CampaignsManager onChange={refreshCounts} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it) => (
              <div key={it.id} className="rounded-xl border border-[#26282e] bg-[#141518] overflow-hidden card-hover flex flex-col">
                {(it.image || it.logo) && (
                  <div className="aspect-[16/9] bg-[#0d0e10] flex items-center justify-center overflow-hidden">
                    <img src={fileUrl(it.image || it.logo)} alt={it.name || it.title} className={it.logo && !it.image ? 'max-h-[70%] max-w-[70%] object-contain' : 'w-full h-full object-cover'} />
                  </div>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-display font-semibold text-[15px] leading-snug">{it.name || it.title}</h3>
                  {it.category && <span className="text-[11px] text-[#f5a524] mt-1">{it.category}</span>}
                  {it.type && <span className="text-[11px] text-[#f5a524] mt-1">{it.type} · {it.year}</span>}
                  {it.price && <span className="text-[12px] text-[#8b8f97] mt-1">{it.price}</span>}
                  <div className="flex gap-2 mt-4 pt-3 border-t border-[#26282e]">
                    <button onClick={() => setEditing(it)} className="flex-1 flex items-center justify-center gap-1.5 text-[13px] text-[#c7cace] hover:text-white bg-[#0d0e10] rounded-md py-2 transition-colors">
                      <Pencil size={14} /> Editează
                    </button>
                    <button onClick={() => onDelete(it.id)} className="flex items-center justify-center text-[#8b8f97] hover:text-[#ef5350] bg-[#0d0e10] rounded-md px-3 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-[14px] text-[#8b8f97] col-span-full">Nicio înregistrare. Apasă „Adaugă”.</p>}
          </div>
        )}
      </main>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#141518] border border-[#26282e] text-white max-w-[520px] max-h-[90vh] overflow-y-auto">
          {editing && (
            <div>
              <DialogTitle asChild><h3 className="font-display font-bold text-[20px] mb-5">{editing.id ? 'Editează' : 'Adaugă'} {TABS.find((t) => t.key === tab).label.toLowerCase()}</h3></DialogTitle>
              <div className="space-y-3">
                {FIELDS[tab].map((f) => (
                  <div key={f.k}>
                    {f.type === 'image' ? (
                      <ImageField
                        label={f.label}
                        value={editing[f.k]}
                        testid={`admin-image-${f.k}`}
                        onChange={(url) => setEditing((s) => ({ ...s, [f.k]: url }))}
                      />
                    ) : (
                      <>
                        <Label className="text-[12px] text-[#9a9ea6]">{f.label}{f.req && ' *'}</Label>
                        {f.area ? (
                          <Textarea rows={3} value={editing[f.k] || ''} data-testid={`admin-field-${f.k}`} onChange={(e) => setEditing((s) => ({ ...s, [f.k]: e.target.value }))} className="mt-1.5 bg-[#0d0e10] border-[#26282e] text-white focus-visible:ring-[#f5a524] resize-none" />
                        ) : (
                          <Input value={editing[f.k] || ''} data-testid={`admin-field-${f.k}`} onChange={(e) => setEditing((s) => ({ ...s, [f.k]: e.target.value }))} className="mt-1.5 bg-[#0d0e10] border-[#26282e] text-white focus-visible:ring-[#f5a524]" />
                        )}
                      </>
                    )}
                  </div>
                ))}
                {(tab === 'products' || tab === 'projects') && (
                  <div className="pt-2">
                    <Label className="text-[12px] text-[#9a9ea6]">Galerie media (foto / filme / certificări / tehnice)</Label>
                    <div className="mt-2">
                      <MediaManager
                        value={editing.media}
                        onChange={(media) => setEditing((s) => ({ ...s, media }))}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setEditing(null)} data-testid="admin-cancel-button" className="btn-ghost rounded-md flex-1 py-2.5 text-[14px]">Anulează</button>
                <button onClick={onSave} data-testid="admin-save-button" className="btn-accent rounded-md flex-1 py-2.5 text-[14px]">Salvează</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Media add dialog removed: media library is independent (see MediaLibrary) */}
    </div>
  );
}

const MEDIA_CATS = [
  { key: 'foto', label: 'Foto', icon: ImageIcon, accept: 'image/*' },
  { key: 'filme', label: 'Filme', icon: Film, accept: 'video/*' },
  { key: 'certificari', label: 'Certificări', icon: Award, accept: 'application/pdf,image/*' },
  { key: 'tehnice', label: 'Tehnice', icon: FileText, accept: 'application/pdf,image/*' },
];

function MediaLibrary({ onChange }) {
  const { toast } = useToast();
  const [files, setFiles] = useState([]);
  const [busyCat, setBusyCat] = useState(null);

  const load = async () => {
    try { setFiles(await listMedia()); } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onUpload = async (cat, fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setBusyCat(cat);
    for (const f of arr) {
      if (f.size > 50 * 1024 * 1024) { toast({ title: 'Fișier prea mare', description: 'Limita este 50 MB.' }); continue; }
      try { await uploadFile(f, cat); } catch (e) { toast({ title: 'Încărcare eșuată', description: 'Încearcă din nou.' }); }
    }
    setBusyCat(null);
    await load();
    onChange && onChange();
    toast({ title: 'Media salvată' });
  };

  const onRemove = async (id) => {
    try { await deleteMedia(id); await load(); onChange && onChange(); toast({ title: 'Șters' }); }
    catch (e) { toast({ title: 'Eroare la ștergere' }); }
  };

  return (
    <div className="space-y-5" data-testid="admin-media-overview">
      {MEDIA_CATS.map((cat) => (
        <MediaCategory
          key={cat.key}
          cat={cat}
          files={files.filter((f) => (f.category || 'foto') === cat.key)}
          busy={busyCat === cat.key}
          onUpload={onUpload}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

function MediaCategory({ cat, files, busy, onUpload, onRemove }) {
  const inputRef = useRef(null);
  const Icon = cat.icon;
  return (
    <div className="rounded-xl border border-[#26282e] bg-[#141518] p-4" data-testid={`admin-media-group-${cat.key}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={17} className="text-[#f5a524]" />
          <h3 className="font-display font-semibold text-[15px]">{cat.label}</h3>
          <span className="text-[11px] rounded-full px-2 py-0.5 bg-[#1e2025] text-[#8b8f97]">{files.length}</span>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`admin-media-add-${cat.key}`}
          className="btn-accent rounded-md px-3 py-2 text-[13px] flex items-center gap-1.5 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Adaugă
        </button>
        <input ref={inputRef} type="file" accept={cat.accept} multiple hidden onChange={(e) => { onUpload(cat.key, e.target.files); e.target.value = ''; }} />
      </div>
      {files.length === 0 ? (
        <p className="text-[13px] text-[#8b8f97]">Niciun fișier încă.</p>
      ) : cat.key === 'foto' ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {files.map((f) => (
            <div key={f.id} className="relative group aspect-square rounded-lg overflow-hidden border border-[#26282e]">
              <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="block w-full h-full">
                <img src={fileUrl(f.url)} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </a>
              <button type="button" onClick={() => onRemove(f.id)} data-testid={`admin-media-del-${f.id}`} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      ) : cat.key === 'filme' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {files.map((f) => (
            <div key={f.id} className="relative group">
              <video src={fileUrl(f.url)} controls className="w-full rounded-lg border border-[#26282e]" />
              <p className="text-[11px] text-[#8b8f97] mt-1 truncate">{f.name}</p>
              <button type="button" onClick={() => onRemove(f.id)} data-testid={`admin-media-del-${f.id}`} className="absolute top-2 right-2 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 bg-[#0d0e10] rounded-md px-3 py-2">
              <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[13px] text-[#c7cace] hover:text-[#f5a524] transition-colors flex-1 min-w-0">
                <FileText size={14} /> <span className="truncate">{f.name || f.url.split('/').pop()}</span>
              </a>
              <button type="button" onClick={() => onRemove(f.id)} data-testid={`admin-media-del-${f.id}`} className="text-[#8b8f97] hover:text-[#ef5350] transition-colors shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadsTable({ leads, onDelete }) {
  if (leads.length === 0)
    return <p className="text-[14px] text-[#8b8f97]">Nicio cerere de ofertă încă. Cererile din formularul de pe site apar aici.</p>;
  return (
    <div className="rounded-xl border border-[#26282e] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13.5px]">
          <thead className="bg-[#0a0b0d] text-[#8b8f97] text-[12px] uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 font-medium">Nume</th>
              <th className="px-4 py-3 font-medium">Contact</th>
              <th className="px-4 py-3 font-medium">Produs</th>
              <th className="px-4 py-3 font-medium">Detalii</th>
              <th className="px-4 py-3 font-medium">Data</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-[#1e2025] bg-[#141518] hover:bg-[#17181c] transition-colors align-top">
                <td className="px-4 py-3">
                  <div className="font-medium text-white">{l.name}</div>
                  {l.company && <div className="text-[12px] text-[#8b8f97]">{l.company}</div>}
                </td>
                <td className="px-4 py-3 text-[#c7cace]">
                  <div>{l.phone}</div>
                  {l.email && <div className="text-[12px] text-[#8b8f97]">{l.email}</div>}
                </td>
                <td className="px-4 py-3"><span className="text-[#f5a524]">{l.product}</span></td>
                <td className="px-4 py-3 text-[#a7abb2] max-w-[240px]">{l.message || '—'}</td>
                <td className="px-4 py-3 text-[#8b8f97] whitespace-nowrap">{new Date(l.createdAt).toLocaleDateString('ro-RO')}</td>
                <td className="px-4 py-3">
                  <button onClick={() => onDelete(l.id)} className="text-[#8b8f97] hover:text-[#ef5350] transition-colors"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UtilizatoriManager({ onChange }) {
  const [sub, setSub] = useState('admin');
  const subCls = (k) => `rounded-md px-4 py-2 text-[13px] font-medium transition-colors ${sub === k ? 'bg-[#f5a524] text-[#131313]' : 'bg-[#141518] border border-[#26282e] text-[#c7cace] hover:text-white'}`;
  return (
    <div data-testid="admin-utilizatori">
      <div className="flex gap-2 mb-6">
        <button onClick={() => setSub('admin')} data-testid="utilizatori-sub-admin" className={subCls('admin')}>Admin</button>
        <button onClick={() => setSub('distribuitori')} data-testid="utilizatori-sub-distribuitori" className={subCls('distribuitori')}>Distribuitori</button>
      </div>
      {sub === 'admin' ? <UsersManager onChange={onChange} /> : <DistribuitoriTable />}
    </div>
  );
}

function DistribuitoriTable() {
  const [rows, setRows] = useState([]);
  useEffect(() => { listDistribuitori().then(setRows).catch(() => {}); }, []);
  return (
    <div data-testid="admin-distribuitori">
      <p className="text-[13px] text-[#8b8f97] mb-4">{rows.length} distribuitori înregistrați (cont propriu sau prin link campanie).</p>
      <div className="rounded-xl border border-[#26282e] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <thead className="bg-[#0a0b0d] text-[#8b8f97] text-[12px] uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-medium">Nume</th>
                <th className="px-4 py-3 font-medium">Firmă</th>
                <th className="px-4 py-3 font-medium">Telefon</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Tip</th>
                <th className="px-4 py-3 font-medium">Înregistrat</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id || r.email} data-testid={`distribuitor-row-${r.email}`} className="border-t border-[#1e2025] bg-[#141518] hover:bg-[#17181c] transition-colors">
                  <td className="px-4 py-3 text-white font-medium">{r.name || '—'}</td>
                  <td className="px-4 py-3 text-[#c7cace]">{r.company || '—'}</td>
                  <td className="px-4 py-3 text-[#c7cace] whitespace-nowrap">{r.phone || '—'}</td>
                  <td className="px-4 py-3 text-[#c7cace]">{r.email}</td>
                  <td className="px-4 py-3"><span className="text-[#f5a524]">{r.source === 'campaign' ? 'Campanie' : 'Cont propriu'}</span></td>
                  <td className="px-4 py-3 text-[#8b8f97] whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString('ro-RO') : '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-[14px] text-[#8b8f97]">Niciun distribuitor înregistrat încă.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UsersManager({ onChange }) {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'edit', id, email }
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setUsers(await listUsers()); } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setDialog({ mode: 'create' }); setEmail(''); setPassword(''); };
  const openEdit = (u) => { setDialog({ mode: 'edit', id: u.id, email: u.email }); setEmail(u.email); setPassword(''); };

  const save = async () => {
    setBusy(true);
    try {
      if (dialog.mode === 'create') {
        if (!email.trim() || !password) { toast({ title: 'Completează email și parolă' }); setBusy(false); return; }
        await createUser(email.trim(), password);
      } else {
        if (!password) { toast({ title: 'Introdu parola nouă' }); setBusy(false); return; }
        await updateUser(dialog.id, password);
      }
      setDialog(null);
      await load(); onChange && onChange();
      toast({ title: 'Salvat' });
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast({ title: 'Eroare', description: typeof msg === 'string' ? msg : 'Încearcă din nou.' });
    }
    setBusy(false);
  };

  const remove = async (u) => {
    try { await deleteUser(u.id); await load(); onChange && onChange(); toast({ title: 'Șters' }); }
    catch (e) {
      const msg = e?.response?.data?.detail;
      toast({ title: 'Eroare la ștergere', description: typeof msg === 'string' ? msg : '' });
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex justify-end mb-4">
        <button onClick={openCreate} data-testid="admin-user-add" className="btn-accent rounded-md px-4 py-2.5 text-[14px] flex items-center gap-1.5">
          <Plus size={16} /> Adaugă utilizator
        </button>
      </div>
      <div className="rounded-xl border border-[#26282e] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <thead className="bg-[#0a0b0d] text-[#8b8f97] text-[12px] uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Creat la</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-[#1e2025] bg-[#141518] hover:bg-[#17181c] transition-colors">
                  <td className="px-4 py-3 text-white font-medium">{u.email}</td>
                  <td className="px-4 py-3 text-[#8b8f97] whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString('ro-RO') : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-4 justify-end">
                      <button onClick={() => openEdit(u)} data-testid={`admin-user-edit-${u.id}`} className="flex items-center gap-1.5 text-[13px] text-[#c7cace] hover:text-white transition-colors"><KeyRound size={14} /> Parolă</button>
                      <button onClick={() => remove(u)} data-testid={`admin-user-del-${u.id}`} className="text-[#8b8f97] hover:text-[#ef5350] transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-[14px] text-[#8b8f97]">Niciun utilizator.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-[#141518] border border-[#26282e] text-white max-w-[420px]">
          {dialog && (
            <div>
              <DialogTitle asChild><h3 className="font-display font-bold text-[20px] mb-5">{dialog.mode === 'create' ? 'Adaugă utilizator' : 'Schimbă parola'}</h3></DialogTitle>
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px] text-[#9a9ea6]">Email</Label>
                  <Input value={email} disabled={dialog.mode === 'edit'} onChange={(e) => setEmail(e.target.value)} placeholder="nume@egea.ro" data-testid="admin-user-email-input" className="mt-1.5 bg-[#0d0e10] border-[#26282e] text-white focus-visible:ring-[#f5a524] disabled:opacity-60" />
                </div>
                <div>
                  <Label className="text-[12px] text-[#9a9ea6]">{dialog.mode === 'create' ? 'Parolă' : 'Parolă nouă'}</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-user-password-input" className="mt-1.5 bg-[#0d0e10] border-[#26282e] text-white focus-visible:ring-[#f5a524]" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setDialog(null)} className="btn-ghost rounded-md flex-1 py-2.5 text-[14px]">Anulează</button>
                <button onClick={save} disabled={busy} data-testid="admin-user-save" className="btn-accent rounded-md flex-1 py-2.5 text-[14px] disabled:opacity-60">{busy ? 'Se salvează...' : 'Salvează'}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const CAMPAIGN_CATEGORIES = ['Schele', 'Suflători', 'Scară pisică'];

function CampaignsManager({ onChange }) {
  const { toast } = useToast();
  const [tokens, setTokens] = useState([]);
  const [form, setForm] = useState({ email: '', name: '', phone: '', company: '', category: CAMPAIGN_CATEGORIES[0] });
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState('');
  const [copied, setCopied] = useState(false);

  const load = async () => { try { setTokens(await listCampaignTokens()); } catch (e) { /* ignore */ } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); toast({ title: 'Link copiat' }); }
    catch (e) { toast({ title: 'Copiază manual', description: text }); }
  };

  const generate = async () => {
    if (!form.email.trim()) { toast({ title: 'Introdu emailul destinatarului' }); return; }
    setBusy(true);
    try {
      const data = await createCampaignToken({ ...form, email: form.email.trim() });
      const link = `${window.location.origin}${data.path}`;
      setLastLink(link);
      await copy(link);
      setForm({ email: '', name: '', phone: '', company: '', category: form.category });
      await load(); onChange && onChange();
      toast({ title: 'Token generat', description: 'Linkul a fost copiat în clipboard.' });
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast({ title: 'Eroare', description: typeof msg === 'string' ? msg : 'Încearcă din nou.' });
    }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await deleteCampaignToken(id); await load(); onChange && onChange(); toast({ title: 'Șters' }); }
    catch (e) { toast({ title: 'Eroare la ștergere' }); }
  };

  const inputCls = 'mt-1.5 bg-[#0d0e10] border-[#26282e] text-white focus-visible:ring-[#f5a524]';

  return (
    <div data-testid="admin-campaigns" className="space-y-6">
      <div className="rounded-xl border border-[#26282e] bg-[#141518] p-5">
        <h3 className="font-display font-semibold text-[16px] mb-1">Generează link de activare</h3>
        <p className="text-[13px] text-[#8b8f97] mb-4">Token unic, valabil 200 de zile. Linkul se afișează o singură dată, la generare.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px] text-[#9a9ea6]">Email destinatar *</Label>
            <Input value={form.email} onChange={set('email')} placeholder="client@firma.ro" data-testid="campaign-email-input" className={inputCls} />
          </div>
          <div>
            <Label className="text-[12px] text-[#9a9ea6]">Categorie campanie</Label>
            <select value={form.category} onChange={set('category')} data-testid="campaign-category-select" className="mt-1.5 w-full h-10 rounded-md bg-[#0d0e10] border border-[#26282e] text-white text-[14px] px-3 focus:outline-none focus:ring-2 focus:ring-[#f5a524]">
              {CAMPAIGN_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[12px] text-[#9a9ea6]">Nume</Label>
            <Input value={form.name} onChange={set('name')} placeholder="Nume contact" data-testid="campaign-name-input" className={inputCls} />
          </div>
          <div>
            <Label className="text-[12px] text-[#9a9ea6]">Telefon</Label>
            <Input value={form.phone} onChange={set('phone')} placeholder="07xx xxx xxx" data-testid="campaign-phone-input" className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[12px] text-[#9a9ea6]">Companie</Label>
            <Input value={form.company} onChange={set('company')} placeholder="Firma (opțional)" data-testid="campaign-company-input" className={inputCls} />
          </div>
        </div>
        <button onClick={generate} disabled={busy} data-testid="campaign-generate-button" className="btn-accent rounded-md px-5 py-2.5 text-[14px] flex items-center gap-1.5 mt-4 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Generează link
        </button>
        {lastLink && (
          <div className="mt-4 flex items-center gap-2 bg-[#0d0e10] border border-[#f5a524]/30 rounded-md px-3 py-2.5" data-testid="campaign-last-link">
            <span className="flex-1 min-w-0 truncate text-[13px] text-[#c7cace]">{lastLink}</span>
            <button onClick={() => copy(lastLink)} className="text-[#f5a524] hover:text-white transition-colors shrink-0" data-testid="campaign-copy-last">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-[#26282e] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <thead className="bg-[#0a0b0d] text-[#8b8f97] text-[12px] uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Categorie</th>
                <th className="px-4 py-3 font-medium">Stare</th>
                <th className="px-4 py-3 font-medium">Creat</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} data-testid={`campaign-row-${t.id}`} className="border-t border-[#1e2025] bg-[#141518] hover:bg-[#17181c] transition-colors">
                  <td className="px-4 py-3 text-white">{t.email}</td>
                  <td className="px-4 py-3"><span className="text-[#f5a524]">{t.category}</span></td>
                  <td className="px-4 py-3 text-[#c7cace]">{t.activated_at ? `Activat (${t.activation_count})` : 'Neactivat'}</td>
                  <td className="px-4 py-3 text-[#8b8f97] whitespace-nowrap">{t.created_at ? new Date(t.created_at).toLocaleDateString('ro-RO') : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(t.id)} data-testid={`campaign-del-${t.id}`} className="text-[#8b8f97] hover:text-[#ef5350] transition-colors"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {tokens.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-[14px] text-[#8b8f97]">Niciun token generat încă.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
